import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { AppConfig } from 'src/app/configs/app.config';
import { AppRoute } from 'src/app/constants/app.route';
import { EventType } from 'src/app/constants/events-type';
import { DestroyNotifierComponent } from 'src/app/core/component/destroy/destroy-notifier.component';
import { AuthFactoryService } from 'src/app/core/services/auth/auth-factory.service';
import { EventManagerService } from 'src/app/core/services/event-manager/event-manager.service';
import { EventContent } from 'src/app/core/services/event-manager/models/event-content';
import { AuthProvider } from 'src/app/core/services/http/general/enums/auth-provider';
import { School } from 'src/app/core/services/http/school/interfaces/school';
import { SchoolService } from 'src/app/core/services/http/school/school.service';
import { PlatformFactoryService } from 'src/app/core/services/platform/platform-factory.service';
import { InHouseProvider } from 'src/app/core/services/provider-builder/interfaces/in-house-provider';
import { ProviderItem } from 'src/app/core/services/provider-builder/interfaces/provider-item';
import { ProviderBuilderService } from 'src/app/core/services/provider-builder/provider-builder.service';
import { FormGroupService } from 'src/app/shared/form-error/form-group.service';
import { environment } from 'src/environments/environment';
import { getFormGroup } from './form-group/form.group';
import { LoginService } from './services/login.service';
import { Alert } from 'src/app/shared/alert/types/alert';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent extends DestroyNotifierComponent implements OnInit {
  @ViewChild('logInForm') logInForm: ElementRef | undefined;

  form = getFormGroup();
  environment = environment;
  appConfig = AppConfig;
  appRoute = AppRoute;
  authProvider = AuthProvider;
  directLoginProvider: ProviderItem | undefined;
  schools: School[] | undefined;
  shouldShowChooseSchoolForm: boolean | undefined;
  providers: ProviderItem[] | undefined;
  inHouseProvider: InHouseProvider | undefined;
  verificationStatus: boolean | undefined;
  verificationMessage = '';
  isVerificationKeyExists: boolean | undefined;
  isUserNotVerified: boolean | undefined;

  constructor(
    private activatedRoute: ActivatedRoute,
    private schoolService: SchoolService,
    private authFactoryService: AuthFactoryService,
    private formGroupService: FormGroupService,
    private providerBuilderService: ProviderBuilderService,
    private eventManager: EventManagerService,
    private loginService: LoginService,
    private platformFactoryService: PlatformFactoryService
  ) {
    super();
    this.activatedRoute.queryParams.subscribe((params: Params) => {
      if (params['verified'] !== undefined) {
        this.verificationStatus = JSON.parse(params['verified']);
        this.verificationMessage = params['message'];
        this.handleEmailVarificationAlert();
      }

      if (params.errorMessage == 'USER_NOT_VERIFIED') {
        this.isUserNotVerified = true;
      }
    });
  }

  private handleEmailVarificationAlert(): void {
    const alert: Alert = {
      type: this.verificationStatus ? 'success' : 'danger',
      dismissOnTimeout: 0,
      translateKey: this.verificationMessage,
      dismissible: true,
      position: 'top-center'
    };
    this.eventManager.broadcast(new EventContent(EventType.AlertMessage, alert));
  }

  async ngOnInit(): Promise<void> {
    await this.handleUserAuthorization();
    this.loginService.onError();
  }

  onLogin(): void {
    this.formGroupService.validateFormGroup(this.form);

    if (this.form.invalid) {
      return;
    }

    if (!this.inHouseProvider) {
      return;
    }

    this.eventManager.broadcast(new EventContent(EventType.PreloaderShow, true));
    this.authFactoryService.service.setVerifierCode(this.inHouseProvider.verifierCode);

    if (this.logInForm) {
      this.platformFactoryService.platformDevice?.onSubmit({
        form: this.logInForm,
        username: this.form.controls.email.value,
        password: this.form.controls.password.value,
        inHouseProvider: this.inHouseProvider
      });
    }
  }

  async onClickProvider(providerItem: ProviderItem): Promise<void> {
    const result = await this.providerBuilderService.getProviderLinkByProvider(providerItem.authProvider);

    if (!result || result.url.length === 0 || result.verifierCode.length === 0) {
      return;
    }

    this.authFactoryService.service.setVerifierCode(result.verifierCode);
    this.loginService.redirectToProviderUrl(result.url);
  }

  onChooseSchool(school: School): void {
    this.loginService.chooseSchool(school, true);
  }

  onLogOut(): void {
    this.authFactoryService.service.logOut();
  }

  private async handleUserAuthorization(): Promise<void> {
    this.eventManager.broadcast(new EventContent(EventType.PreloaderShow, false));

    this.shouldShowChooseSchoolForm = this.authFactoryService.service.getToken().length > 0;
    if (this.shouldShowChooseSchoolForm) {
      this.schoolService
      .getAll()
      .pipe(takeUntil(this.destroyNotify))
      .subscribe({
        next: async (schools) => {
          try {
            if (await this.loginService.automaticallyChooseSchool(schools)) {
              return;
            }
            this.schools = schools;
          } catch (error) {
            console.error('Error during automatic school selection:', error);
          }
        },
        error: (err) => {
          console.error('Error fetching schools:', err);
        }
      });
    } else {
      await this.initProviders();
    }
  }

  private async initProviders(): Promise<void> {
    this.inHouseProvider = await this.providerBuilderService.getInHouseProvider();
    this.providers = await this.providerBuilderService.getProviders();

    if (this.routePath !== AppRoute.Login) {
      this.directLoginProvider = this.providers?.find((provider: ProviderItem) => {
        return provider.dedicatedRouterPath === this.routePath;
      });
    }
  }

  private get routePath() {
    return this.activatedRoute.snapshot.parent?.routeConfig?.path;
  }
}
